import { Alert } from '@creditornot/cb-alert';
import { Button, IconButton } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { EyeFilled, EyeOff } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { Modal, ModalContent, ModalFooter } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { createMenuApiUser, resetPasswordOfMenuApiUser } from 'apis/restaurant-api/merchant-admin/menu-api-users/api';
import { APIErrorNotification, RFFFieldErrorWrapper } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';

import {
  MenuApiCredentials,
  NullableMenuApiCredentialMetadata,
} from '../../../apis/restaurant-api/merchant-admin/menu-api-users/types';
import { isValidUsername } from '../validation';

type FormData = {
  username: string;
};

type ManageModalProps = {
  isOpen: boolean;
  merchantId: string;
  onClose: () => void;
  credentials: NullableMenuApiCredentialMetadata;
};

const FooterContent = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const InfomationMessage = styled(Alert).attrs({ variant: 'info' })`
  margin-block-start: 8px;
`;

const ModalButton = styled(Button)`
  margin: 0 8px;
`;

const Eye = styled.span``;

const PasswordInputWrapper = styled.div`
  display: flex;

  ${Eye} {
    margin-inline-start: 8px;
  }
`;

const createUsernameSchema = () => {
  const username = yup
    .string()
    .max(32)
    .test('validUsernameFormat', { id: 'merchant-integrations.valid-menu-api-username' }, isValidUsername)
    .required({ id: 'validation.required' });

  return yup.object({
    username,
  });
};

const formId = 'menu-api-user-form';

const MenuApiUserModal = ({ onClose, isOpen, merchantId, credentials }: ManageModalProps) => {
  const intl = useIntl();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState<string>('');
  const usernameValidationSchema = useMemo(
    () => (!credentials?.username ? validateYupSchema(createUsernameSchema()) : undefined),
    [credentials],
  );
  const cleanClose = () => {
    setPassword('');
    onClose();
  };

  const createResetPasswordMutation = useMutation(
    (data: FormData) =>
      credentials?.username
        ? resetPasswordOfMenuApiUser(merchantId, credentials?.username.trim())
        : createMenuApiUser(merchantId, data.username.trim()),
    {
      onSuccess: (result: MenuApiCredentials) => {
        credentials = result;
        setPassword(result.password);
        return {};
      },
      onError: (error: AxiosError) => {
        const errorMessage =
          error.response?.status === 409
            ? 'merchant-integrations.menu-api-access-conflict'
            : 'errors.unknown-error-retry-contact';
        error.message = intl.formatMessage({ id: errorMessage });
      },
    },
  );

  return (
    <Modal
      open={isOpen}
      onDismiss={cleanClose}
      sizePreset="centered:large"
      title={
        credentials?.username
          ? intl.formatMessage({ id: 'merchant-integrations.menu-api.reset' })
          : intl.formatMessage({ id: 'merchant-integrations.menu-api.setup' })
      }
      ContentComponent={null}
    >
      <Form<FormData>
        onSubmit={data => createResetPasswordMutation.mutate(data)}
        validate={usernameValidationSchema}
        render={({ handleSubmit, pristine, hasValidationErrors, submitting }) => (
          <>
            <ModalContent>
              <form onSubmit={handleSubmit} id={formId}>
                {password && <FormattedMessage id="merchant-integrations.menu-api.generation-notice" />}
                {credentials?.username && <FormattedMessage id="merchant-integrations.menu-api.reset-notice" />}
                {!credentials?.username && <FormattedMessage id="merchant-integrations.menu-api.setup-notice" />}

                <FormRow
                  required
                  horizontalPadding
                  label={intl.formatMessage({ id: 'merchant-integrations.username' })}
                  labelAlign="center"
                >
                  <Field name="username">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <>
                            <Input {...input} value={credentials?.username} id="api-username" autoComplete="off" />
                            {!credentials?.username && (
                              <InfomationMessage>
                                <FormattedMessage id="merchant-integrations.menu-api-generate-info" />
                              </InfomationMessage>
                            )}
                          </>
                        )}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </FormRow>
                {password && (
                  <>
                    <FormRow
                      horizontalPadding
                      label={intl.formatMessage({ id: 'merchant-integrations.password' })}
                      labelAlign="center"
                    >
                      <PasswordInputWrapper>
                        <Input value={password} type={passwordVisible ? 'text' : 'password'} readOnly />
                        <Eye>
                          {passwordVisible ? (
                            <IconButton
                              variant="neutral"
                              size="small"
                              background="transparent"
                              onClick={() => setPasswordVisible(!passwordVisible)}
                            >
                              <EyeOff />
                            </IconButton>
                          ) : (
                            <IconButton
                              variant="neutral"
                              size="small"
                              background="transparent"
                              onClick={() => setPasswordVisible(!passwordVisible)}
                            >
                              <EyeFilled />
                            </IconButton>
                          )}
                        </Eye>
                      </PasswordInputWrapper>
                    </FormRow>
                  </>
                )}
              </form>
            </ModalContent>

            <StyledModalFooter>
              {createResetPasswordMutation.isError && (
                <APIErrorNotification error={createResetPasswordMutation.error.message} style={{ marginBlockEnd: 8 }} />
              )}
              <FooterContent>
                <ModalButton
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    if (password) {
                      setPassword('');
                    }
                    cleanClose();
                  }}
                  disabled={createResetPasswordMutation.isLoading}
                  loading={createResetPasswordMutation.isLoading}
                >
                  {password ? <FormattedMessage id="state.done" /> : <FormattedMessage id="state.cancel" />}
                </ModalButton>

                {!credentials?.username ? (
                  <ModalButton
                    type="submit"
                    form={formId}
                    variant="primary"
                    size="small"
                    disabled={pristine || hasValidationErrors || createResetPasswordMutation.isLoading || !!password}
                    loading={createResetPasswordMutation.isLoading}
                  >
                    <FormattedMessage id="merchant-integrations.menu-api.generate-action" />
                  </ModalButton>
                ) : (
                  <ModalButton
                    type="submit"
                    form={formId}
                    variant="primary"
                    size="small"
                    disabled={hasValidationErrors || submitting || !!password}
                    loading={submitting}
                  >
                    <FormattedMessage id="merchant-integrations.menu-api.reset-action" />
                  </ModalButton>
                )}
              </FooterContent>
            </StyledModalFooter>
          </>
        )}
      />
    </Modal>
  );
};

export default MenuApiUserModal;
